import * as React from 'react';
import styled from 'styled-components';

import { Row, Col } from 'common/components/layout-styles';
import { useSelectedRows, useSelectedRowsDispatch } from 'common/contexts';
import type { EventProcedureStepAPIType } from 'security-app/hooks/api/eventProceduresAPI.types';
import { MarkdownPreview } from 'components/common/MarkdownEditor';
import { Alert } from 'components/bootstrap';

const SectionHeaderRow = styled(Row)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.input.border};
  padding-bottom: ${({ theme }) => theme.spacings.sm};
`;

function useTableElements(selectedSteps: Array<EventProcedureStepAPIType>) {
  const selectedRows = useSelectedRows();
  const selectedRowsDispatch = useSelectedRowsDispatch();

  const renderExpanded = React.useCallback(
    (step: EventProcedureStepAPIType) => (
      <Col>
        <SectionHeaderRow>
          <h5>Description</h5>
        </SectionHeaderRow>
        {step?.description ? (
          <MarkdownPreview show maxHeight={400} noBackground noBorder value={step?.description} noPadding />
        ) : (
          <Alert style={{ width: '100%' }}>No description set</Alert>
        )}
      </Col>
    ),
    [],
  );

  const expandedSections = React.useMemo(
    () => ({
      restFieldsExpandedSection: {
        title: null,
        content: renderExpanded,
      },
    }),
    [renderExpanded],
  );

  const bulkSelection = React.useMemo(
    () => ({
      onChangeSelection: (stepIds: Array<string>, list: Array<EventProcedureStepAPIType>) => {
        const currentSteps = [...selectedSteps, ...selectedRows, ...list].reduce(
          (acc: Array<EventProcedureStepAPIType>, step: EventProcedureStepAPIType) => {
            if (!acc.find((s) => s.id === step.id)) acc.push(step);

            return acc;
          },
          [],
        );

        const auxSelectedSteps =
          stepIds.length > 0
            ? stepIds.map((stepId: string) =>
                currentSteps.find((step: EventProcedureStepAPIType) => step.id === stepId),
              )
            : [];
        selectedRowsDispatch({ type: 'upsert', payload: auxSelectedSteps });
      },
      actions: <div />,
      initialSelection: selectedSteps?.map((step: EventProcedureStepAPIType) => step.id),
    }),
    [selectedRowsDispatch, selectedSteps, selectedRows],
  );

  return {
    expandedSections,
    bulkSelection,
  };
}

export default useTableElements;
